import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getDoc } from "firebase/firestore";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import SummaryCards from "../../../DesignSystem/SummaryCards/SummaryCards";
import RadioGroup from "../../../DesignSystem/Inputs/RadioGroup";
import SearchField from "../../../DesignSystem/Inputs/SearchField";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import Banner from "../../../DesignSystem/Banner/Banner";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { useAdviserCharges } from "../context/AdviserChargesContext";
import { useAdviserList } from "../../../../context/Adviser/Utils/AdviserEmailList";
import { useUserEmail } from "../../../SignIn/UserEmailContext";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import { getIODetailsRef } from "../../../Adviser/Tabs/IODetails/ioDetailsService";
import {
  getFCIStatementsPage,
} from "../../FCIStatements/fciStatementsService";
import { subscribeQuilterStatement } from "../services/quilterStatementService";
import { normaliseQuilterRows } from "../utils/quilterStatementParser";
import { subscribeFinplanStatement } from "../services/finplanStatementService";
import { finplanCommissionRows } from "../utils/finplanAdviserLinks";
import { subscribeSuspenseStatement } from "../services/suspenseStatementService";
import { normaliseSuspenseRows } from "../utils/suspenseStatementParser";
import { subscribeIORecipientMappings } from "../services/ioRecipientMappingService";
import { mappedRecipientIdsByAdviser } from "../utils/ioRecipientTargets";
import { adviserIoRecipientIds } from "../utils/ioRecipientMapping";
import { buildCommissionBreakdown } from "../components/CommissionStatementPDF/utils/commissionBreakdown";
import { buildFeesAndChargesBreakdown } from "../components/CommissionStatementPDF/utils/feesAndChargesBreakdown";
import {
  getIncomePeriodStartMonthKey,
  monthKeyFromDate,
} from "../utils/incomePeriod";
import withMonthlyPaymentsAccess from "../components/withMonthlyPaymentsAccess";
import { ReactComponent as ArrowRightIcon } from "../../../../assets/icons/finance/CrossLaunch.svg";
import { ReactComponent as EditIcon } from "../../../../assets/icons/finance/Edit.svg";
import { ReactComponent as SortIcon } from "../../../../assets/icons/paraplanningDashboard/Sort.svg";
import { loadAMLClientName } from "../../AMLStatements.js/amlDashboard";
import { subscribeMonthlyManualPayments } from "../services/manualPaymentService";
import { calculateAccountBalances } from "../utils/accountBalances";
import { manualPaymentFeeEntries } from "../utils/manualPaymentCharges";
import { formatMonthKey } from "../utils/chargeDates";
import { paymentsForMonth } from "../utils/paymentDates";
import { canOverrideClosedChargeMonths } from "../utils/closedChargeMonths";
import { feeEntryDescription } from "../components/CommissionStatementPDF/utils/feesAndChargesBreakdown";
import "../../../../css/Finance/MonthlyPayments.css";
import "../../../../css/Marketing/WebsiteCharges.css";

const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    Number(value || 0),
  );
const keyOf = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const formatDate = (value) => {
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
};
const sortableValue = (row, key) => {
  if (key === "chargeType") return row.manualChargeType || row.chargeType || "";
  if (key === "amount") return Number(row.amountCharged || 0);
  if (["date", "chargeFrom", "chargeTo"].includes(key)) {
    const date = new Date(row[key]);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }
  return String(row[key] || "").toLowerCase();
};

const MiscellaneousChargesTable = ({ title, rows, sort, onSort, onEdit, locked }) => {
  const isRecurring = title === "Recurring Charges";
  const columns = isRecurring
    ? [
        ["chargeType", "Charge Type"],
        ["date", "Date"],
        ["description", "Charge Description"],
        ["chargeFrom", "From"],
        ["chargeTo", "To"],
        ["repaymentInstalments", "Repayment"],
        ["amount", "Amount"],
      ]
    : [
        ["chargeType", "Charge Type"],
        ["date", "Date"],
        ["description", "Charge Description"],
        ["amount", "Amount"],
      ];

  return (
    <div className="fit-column width-100 ">
      <div className="bg-yellow-400 py-s px-s">
        <p className="FP_Help_Text_Semibold uppercase text-primary-1">
          {title}
        </p>
      </div>
      <div className="website-table-wrap table-scroll-container">
        <table className="website-table" aria-label={title}>
          <thead>
            <tr>
              {columns.map(([key, label]) => (
                <th
                  key={key}
                  className={`FP_Help_Text_Semibold uppercase ${key === "amount" ? "website-charge-group-divider text-align-right" : ""}`}
                >
                  <button
                    type="button"
                    className="table-heading-content table-sort-button"
                    onClick={() => onSort(key)}
                    aria-label={`Sort ${label}`}
                    aria-pressed={sort.key === key}
                  >
                    {label} <SortIcon aria-hidden="true" />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row) => (
                <tr key={row.id}>
                  {columns.map(([key]) => {
                    if (key === "chargeType") {
                      return (
                        <td key={key} className="FP_Help_Text black">
                          {row.manualChargeType || row.chargeType}
                        </td>
                      );
                    }
                    if (
                      key === "date" ||
                      key === "chargeFrom" ||
                      key === "chargeTo"
                    ) {
                      return (
                        <td key={key} className="FP_Help_Text black">
                          {formatDate(row[key])}
                        </td>
                      );
                    }
                    if (key === "amount") {
                      const isManualCharge = row.recordType === "manual_charge";
                      return (
                        <td
                          key={key}
                          className="FP_Help_Text black website-charge-group-divider"
                        >
                          <div className="row width-full justify-flex-end align-center gap-m">
                            <span>{money(row.amountCharged)}</span>
                            {isManualCharge && !locked ? (
                              <button
                                type="button"
                                className="table-row-action"
                                aria-label={`Edit ${row.manualChargeType || row.chargeType}`}
                                onClick={() => onEdit(row)}
                              >
                                <EditIcon aria-hidden="true" />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      );
                    }
                    return (
                      <td key={key} className="FP_Help_Text black">
                        {row[key] || "-"}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="FP_Content">
                  No {title.toLowerCase()}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdviserChargeBreakdown = () => {
  const navigate = useNavigate();
  const { userEmail } = useUserEmail();
  const { userPermissions = [] } = useUserPermissions();
  const { adviserEmail: routeAdviser } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    selectedMonth,
    setSelectedMonth,
    monthOptions,
    incomePeriods,
    incomePeriodsError,
    chargeRecords,
    loadedChargeRecords,
    chargesLoading,
    advisersByEmail,
    selectedMonthClosed,
  } = useAdviserCharges();
  const { advisers } = useAdviserList();
  const email = keyOf(routeAdviser);
  const requestedMonth = searchParams.get("month");
  const canOverrideClosedCharges = canOverrideClosedChargeMonths(userPermissions);
  const chargesLocked = selectedMonthClosed && !canOverrideClosedCharges;
  const sourceView = searchParams.get("view") || "monthly";
  const isBalanceView = ["opening-balances", "closing-balances"].includes(sourceView);
  const sourceViewLabel =
    sourceView === "advances"
      ? "Advances"
      : sourceView === "additional-payments"
        ? "Additional Payments"
        : sourceView === "closing-balances"
          ? "Closing Balances"
        : sourceView === "opening-balances"
          ? "Opening Balances"
          : "Monthly Charges";
  const [payments, setPayments] = useState([]);
  const [paymentSorts, setPaymentSorts] = useState({
    advances: { key: "date", direction: "asc" },
    additional: { key: "date", direction: "asc" },
  });
  const advancesRef = useRef(null);
  const additionalPaymentsRef = useRef(null);
  const openingBalancesRef = useRef(null);
  const monthlyChargesRef = useRef(null);
  const miscellaneousRef = useRef(null);
  const paymentsRef = useRef(null);
  const [amlClientNames, setAmlClientNames] = useState({});
  const [search, setSearch] = useState("");
  const [miscSorts, setMiscSorts] = useState({
    oneTime: { key: "date", direction: "asc" },
    recurring: { key: "date", direction: "asc" },
  });
  const [monthlySort, setMonthlySort] = useState({
    key: "date",
    direction: "asc",
  });
  const [ioStatementLoading, setIoStatementLoading] = useState(true);
  const [ioStatementError, setIoStatementError] = useState("");
  const [ioStatementRows, setIoStatementRows] = useState([]);
  const [ioStatementMonth, setIoStatementMonth] = useState("");
  const [ioRecipientIds, setIoRecipientIds] = useState([]);
  const [quilterStatement, setQuilterStatement] = useState(null);
  const [quilterLoading, setQuilterLoading] = useState(true);
  const [quilterLoadedMonth, setQuilterLoadedMonth] = useState("");
  const [finplanStatement, setFinplanStatement] = useState(null);
  const [finplanLoading, setFinplanLoading] = useState(true);
  const [finplanLoadedMonth, setFinplanLoadedMonth] = useState("");
  const [suspenseStatement, setSuspenseStatement] = useState(null);
  const [suspenseLoading, setSuspenseLoading] = useState(true);
  const [suspenseLoadedMonth, setSuspenseLoadedMonth] = useState("");
  const [ioRecipientMappings, setIoRecipientMappings] = useState({});

  useEffect(() => {
    if (
      monthOptions.some((option) => option.value === requestedMonth) &&
      selectedMonth !== requestedMonth
    )
      setSelectedMonth(requestedMonth);
  }, [monthOptions, requestedMonth, selectedMonth, setSelectedMonth]);
  useEffect(() => subscribeMonthlyManualPayments(setPayments, () => setPayments([])), []);
  useEffect(
    () => subscribeIORecipientMappings(setIoRecipientMappings, () => setIoRecipientMappings({})),
    [],
  );
  useEffect(
    () => {
      let active = true;
      setQuilterLoading(true);
      setQuilterStatement(null);
      const unsubscribe = subscribeQuilterStatement(
        selectedMonth,
        (statement) => {
          if (!active) return;
          setQuilterStatement(statement);
          setQuilterLoadedMonth(selectedMonth);
          setQuilterLoading(false);
        },
        () => {
          if (!active) return;
          setQuilterStatement(null);
          setQuilterLoadedMonth(selectedMonth);
          setQuilterLoading(false);
        },
      );
      return () => {
        active = false;
        unsubscribe();
      };
    },
    [selectedMonth],
  );
  useEffect(
    () => {
      let active = true;
      setFinplanLoading(true);
      setFinplanStatement(null);
      const unsubscribe = subscribeFinplanStatement(
        selectedMonth,
        (statement) => {
          if (!active) return;
          setFinplanStatement(statement);
          setFinplanLoadedMonth(selectedMonth);
          setFinplanLoading(false);
        },
        () => {
          if (!active) return;
          setFinplanStatement(null);
          setFinplanLoadedMonth(selectedMonth);
          setFinplanLoading(false);
        },
      );
      return () => {
        active = false;
        unsubscribe();
      };
    },
    [selectedMonth],
  );
  useEffect(
    () => {
      let active = true;
      setSuspenseLoading(true);
      setSuspenseStatement(null);
      const unsubscribe = subscribeSuspenseStatement(
        selectedMonth,
        (statement) => {
          if (!active) return;
          setSuspenseStatement(statement);
          setSuspenseLoadedMonth(selectedMonth);
          setSuspenseLoading(false);
        },
        () => {
          if (!active) return;
          setSuspenseStatement(null);
          setSuspenseLoadedMonth(selectedMonth);
          setSuspenseLoading(false);
        },
      );
      return () => {
        active = false;
        unsubscribe();
      };
    },
    [selectedMonth],
  );
  useEffect(() => {
    const target = sourceView === "advances" ? advancesRef.current : sourceView === "additional-payments" ? additionalPaymentsRef.current : isBalanceView ? openingBalancesRef.current : null;
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [isBalanceView, sourceView, payments.length]);

  const adviser = useMemo(() => {
    const fromList = advisers.find(
      (item) => keyOf(item.email || item.id) === email,
    );
    const fromCharges = advisersByEmail[email]?.adviser;
    return {
      name:
        fromList?.name ||
        fromCharges?.adviser ||
        chargeRecords.find((row) => keyOf(row.adviserEmail) === email)
          ?.adviser ||
        routeAdviser,
      brand:
        fromList?.brandName ||
        fromCharges?.brandName ||
        fromCharges?.practice ||
        "-",
      email:
        fromList?.email || fromCharges?.adviserEmail || email || routeAdviser,
      id: fromList?.adviserId || fromCharges?.adviserId || "",
    };
  }, [advisers, advisersByEmail, chargeRecords, email, routeAdviser]);
  useEffect(() => {
    let active = true;
    setIoStatementLoading(true);
    setIoStatementError("");
    setIoStatementRows([]);
    setIoRecipientIds([]);
    if (!adviser.email || (incomePeriods === null && !incomePeriodsError)) {
      if (!adviser.email) {
        setIoStatementMonth(selectedMonth);
        setIoStatementLoading(false);
      }
      return () => { active = false; };
    }
    if (incomePeriodsError) {
      setIoStatementRows([]);
      setIoRecipientIds([]);
      setIoStatementMonth(selectedMonth);
      setIoStatementError("Unable to load IO income periods.");
      setIoStatementLoading(false);
      return () => { active = false; };
    }
    const latestClosed = incomePeriods?.[0] || {};
    const currentIncomeMonth = monthKeyFromDate(
      latestClosed.endDatetime || latestClosed.endDate || latestClosed.incomePeriodEndDatetime,
    );
    const matchingPeriod = (incomePeriods || []).find(
      (period) => getIncomePeriodStartMonthKey(period) === selectedMonth,
    );
    const period = selectedMonth === currentIncomeMonth
      ? "current"
      : String(matchingPeriod?.incomePeriodId || "");

    if (!adviser.email || !period) {
      setIoStatementRows([]);
      setIoRecipientIds([]);
      setIoStatementMonth(selectedMonth);
      setIoStatementLoading(false);
      return () => { active = false; };
    }

    Promise.all([
      getDoc(getIODetailsRef(adviser.email)),
      getFCIStatementsPage(1, 100000, userEmail, {
        _period: period,
        _sort: "PaymentCreated",
        _direction: "desc",
        _export: "true",
      }),
    ])
      .then(([snapshot, result]) => {
        if (!active) return;
        const ioDetails = snapshot.exists() ? snapshot.data() : {};
        setIoRecipientIds(adviserIoRecipientIds(ioDetails));
        setIoStatementRows(result.rows || []);
        setIoStatementMonth(selectedMonth);
        setIoStatementLoading(false);
      })
      .catch((error) => {
        if (!active) return;
        setIoRecipientIds([]);
        setIoStatementRows([]);
        setIoStatementMonth(selectedMonth);
        setIoStatementError(error.message || "Unable to load IO income.");
        setIoStatementLoading(false);
      });
    return () => { active = false; };
  }, [adviser.email, incomePeriods, incomePeriodsError, selectedMonth, userEmail]);
  const records = useMemo(
    () => chargeRecords.filter(
      (row) =>
        keyOf(row.adviserEmail) === email &&
        row.excludeFromBalance !== true,
    ),
    [chargeRecords, email],
  );
  const adviserPayments = paymentsForMonth(payments, selectedMonth).filter(
    (payment) => keyOf(payment.adviserEmail) === email,
  );
  const advances = adviserPayments.filter((payment) => payment.paymentType === "advance");
  const additionalPayments = adviserPayments.filter((payment) => payment.paymentType === "additional_payment");
  const accountBalances = useMemo(
    () => calculateAccountBalances({ records: loadedChargeRecords, payments, month: selectedMonth }),
    [loadedChargeRecords, payments, selectedMonth],
  );
  const openingBalance = Number(accountBalances.balances.get(email) || 0);
  const mappedIoIds = mappedRecipientIdsByAdviser(ioRecipientMappings).get(email) || [];
  const recipientIds = new Set([...ioRecipientIds, ...mappedIoIds].map((id) => String(id).trim().toLowerCase()));
  const ioCommissionRows = ioStatementRows.filter((row) => recipientIds.has(String(
    row.RecipientCrmId ?? row.RecipientUserId ?? row.RecipientId ?? row.AdviserId ?? "",
  ).trim().toLowerCase()));
  const ioIncome = buildCommissionBreakdown(ioCommissionRows).total;
  const quilterLinks = new Map(
    (quilterStatement?.adviserLinks || []).map((link) => [
      keyOf(link.sourceAdviserName),
      keyOf(link.email),
    ]),
  );
  const advisersByName = new Map(
    advisers.map((item) => [keyOf(item.name), keyOf(item.email || item.id)]),
  );
  const quilterCommissionRows = normaliseQuilterRows(quilterStatement?.rows || [])
    .filter((row) => {
      const mappedEmail = quilterLinks.get(keyOf(row.SourceAdviserName || row.MappedAdviserName))
        || advisersByName.get(keyOf(row.MappedAdviserName))
        || "";
      return mappedEmail === email || (!mappedEmail && keyOf(row.MappedAdviserName) === keyOf(adviser.name));
    });
  const finplanRows = finplanCommissionRows({
    statement: finplanStatement,
    advisers,
    statementMonth: selectedMonth,
  }).filter((row) =>
    keyOf(row.RecipientEmail) === email ||
    (!row.RecipientEmail && keyOf(row.Recipient) === keyOf(adviser.name)),
  );
  const totalCommission = buildCommissionBreakdown([
    ...ioCommissionRows,
    ...quilterCommissionRows,
    ...finplanRows,
  ]).total;
  const suspenseLinks = new Map(
    (suspenseStatement?.adviserLinks || []).map((link) => [
      keyOf(link.sourceAdviserName),
      keyOf(link.email),
    ]),
  );
  const suspenseRows = normaliseSuspenseRows(suspenseStatement?.rows || [])
    .map((row) => ({
      ...row,
      adviserEmail: suspenseLinks.get(keyOf(row.SourceAdviserName))
        || advisersByName.get(keyOf(row.MappedAdviserName))
        || "",
    }))
    .filter((row) => keyOf(row.adviserEmail) === email);
  const manualPaymentCharges = manualPaymentFeeEntries(adviserPayments);
  const totalCharged = -buildFeesAndChargesBreakdown(
    [...records, ...manualPaymentCharges],
    suspenseRows,
  ).total;
  const closingBalance = openingBalance + totalCommission + totalCharged;
  const statementValuesLoading =
    chargesLoading ||
    ioStatementLoading || ioStatementMonth !== selectedMonth ||
    quilterLoading || quilterLoadedMonth !== selectedMonth ||
    finplanLoading || finplanLoadedMonth !== selectedMonth ||
    suspenseLoading || suspenseLoadedMonth !== selectedMonth;
  const selectMonth = (event) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("month", event.target.value);
    setSearchParams(nextParams);
  };
  useEffect(() => {
    if (chargesLoading || !email) return;
    // console.groupCollapsed(
    //   `[Opening Balance Audit] ${adviser.name} (${email}) - ${selectedMonth}: ${money(openingBalance)}`,
    // );
    // console.table(entries.map((entry) => ({
    //   month: entry.month,
    //   type: entry.type,
    //   category: entry.category,
    //   description: entry.description,
    //   movement: money(entry.movement),
    //   runningBalance: money(entry.runningBalance),
    //   recordId: entry.recordId,
    //   sourcePath: entry.sourcePath || "",
    // })));
    // console.log("Opening balance total", {
    //   adviser: adviser.name,
    //   adviserEmail: email,
    //   selectedMonth,
    //   openingBalance,
    //   entryCount: entries.length,
    // });
    // console.groupEnd();
  }, [accountBalances, adviser.name, chargesLoading, email, openingBalance, selectedMonth]);
  const sortedPayments = (rows, table) => {
    const sort = paymentSorts[table];
    return [...rows].sort((left, right) => {
      const amount = (payment) => payment.paymentType === "additional_payment"
        ? Math.abs(Number(payment.amountPaid || 0))
        : Number(payment.amountPaid || 0);
      const leftValue = sort.key === "amountPaid" ? amount(left) : String(left[sort.key] || "");
      const rightValue = sort.key === "amountPaid" ? amount(right) : String(right[sort.key] || "");
      const comparison = typeof leftValue === "number" ? leftValue - rightValue : leftValue.localeCompare(rightValue, "en-GB");
      return sort.direction === "asc" ? comparison : -comparison;
    });
  };
  const sortPayments = (table, key) => setPaymentSorts((current) => ({
    ...current,
    [table]: {
      key,
      direction: current[table].key === key && current[table].direction === "asc" ? "desc" : "asc",
    },
  }));
  useEffect(() => {
    let active = true;
    const missingClients = records.filter(
      (row) => row.chargeType === "AML Checks" && !row.client && row.__ref,
    );
    if (!missingClients.length)
      return () => {
        active = false;
      };

    Promise.all(
      missingClients.map(async (row) => [row.id, await loadAMLClientName(row)]),
    ).then((entries) => {
      if (active) {
        setAmlClientNames((current) => ({
          ...current,
          ...Object.fromEntries(entries),
        }));
      }
    });
    return () => {
      active = false;
    };
  }, [records]);
  const categories = [
    ["Monthly Fees", "Monthly Fees", "cream"],
    ["AML Checks", "AML Checks", "blue"],
    ["Paraplanning", "Paraplanning", "purple"],
    ["Website & Graphics", "Website & Graphics", "crimson"],
  ];
  const scrollToSection = (reference) =>
    reference.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const summaryPeriod = formatMonthKey(selectedMonth).toUpperCase();
  const cards = [
    ...categories,
    ["Miscellaneous", "Miscellaneous", "green"],
  ].map(([type, label, tone]) => ({
    id: type,
    label,
    tone,
    period: summaryPeriod,
    onClick: () =>
      scrollToSection(
        type === "Miscellaneous" ? miscellaneousRef : monthlyChargesRef,
      ),
    value: money(
      records
        .filter((row) => row.chargeType === type)
        .reduce((sum, row) => sum + Number(row.amountCharged || 0), 0),
    ),
  }));
  cards.push({
    id: "payments",
    label: "Payments",
    tone: "pink",
    period: summaryPeriod,
    onClick: () => scrollToSection(paymentsRef),
    value: money(
      adviserPayments.reduce(
        (sum, payment) => sum + (payment.paymentType === "additional_payment"
          ? Math.abs(Number(payment.amountPaid || 0))
          : Number(payment.amountPaid || 0)),
        0,
      ),
    ),
  });
  cards.push({
    id: "io-income",
    label: "Total IO Income",
    tone: "blue",
    period: summaryPeriod,
    onClick: () => scrollToSection(openingBalancesRef),
    value: money(ioIncome),
  });
  const searchedRecords = records.filter((row) =>
    [
      row.chargeType,
      row.manualChargeType,
      row.description,
      row.client,
      amlClientNames[row.id],
      row.paraplanner,
      row.assignedTo,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const monthlyRecords = searchedRecords.filter((row) =>
    [...categories.map(([type]) => type), "Technology"].includes(
      row.chargeType,
    ),
  );
  const miscellaneous = searchedRecords.filter(
    (row) =>
      ![...categories.map(([type]) => type), "Technology"].includes(
        row.chargeType,
      ),
  );
  const sortMiscellaneous = (type, key) => {
    setMiscSorts((current) => {
      const existing = current[type];
      return {
        ...current,
        [type]: {
          key,
          direction:
            existing.key === key && existing.direction === "asc"
              ? "desc"
              : "asc",
        },
      };
    });
  };
  const sortedMiscellaneous = (rows, type) => {
    const sort = miscSorts[type];
    return [...rows].sort((left, right) => {
      const leftValue = sortableValue(left, sort.key);
      const rightValue = sortableValue(right, sort.key);
      const comparison =
        typeof leftValue === "number"
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue), "en-GB");
      return sort.direction === "asc" ? comparison : -comparison;
    });
  };
  const oneTimeCharges = miscellaneous.filter(
    (row) => row.billingType !== "recurring" && row.recurrence !== "recurring",
  );
  const recurringCharges = miscellaneous.filter(
    (row) => row.billingType === "recurring" || row.recurrence === "recurring",
  );
  const chargePageUrl = (chargeId = "") =>
    `${APP_ROUTES.CHARGES_BREAKDOWN}/${encodeURIComponent(adviser.email)}/charge?month=${selectedMonth}${chargeId ? `&charge=${encodeURIComponent(chargeId)}` : ""}`;
  const paymentPageUrl = (paymentId = "", view = sourceView) =>
    `${APP_ROUTES.CHARGES_BREAKDOWN}/${encodeURIComponent(adviser.email)}/payment?month=${selectedMonth}&view=${view === "advances" ? "advances" : "additional-payments"}${paymentId ? `&payment=${encodeURIComponent(paymentId)}` : ""}`;
  const sortedMonthlyRecords = [...monthlyRecords].sort((left, right) => {
    const leftValue = sortableValue(left, monthlySort.key);
    const rightValue = sortableValue(right, monthlySort.key);
    const comparison =
      typeof leftValue === "number"
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), "en-GB");
    return monthlySort.direction === "asc" ? comparison : -comparison;
  });
  const toggleMonthlySort = (key) => {
    setMonthlySort((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  return (
    <div className="Main-Page">
      <Helmet>
        <title>{adviser.name} Charges</title>
      </Helmet>
      <Sidebar isSetExpanded={true} />
      <main className="Main-Container monthly-payments-page column gap-l">
        <Breadcrumb
          items={isBalanceView
            ? [
                { label: "Finance", to: "/Department/Finance" },
                {
                  label: "Monthly Payments",
                  to: `${APP_ROUTES.MONTHLY_PAYMENTS}/${sourceView}?month=${selectedMonth}`,
                },
                {
                  label: sourceViewLabel,
                  to: `${APP_ROUTES.MONTHLY_PAYMENTS}/${sourceView}?month=${selectedMonth}`,
                },
                { label: adviser.name },
              ]
            : [
                { label: "Finance", to: "/Department/Finance" },
                {
                  label: "Monthly Payments",
                  to: APP_ROUTES.CHARGES_SUMMARY_DASHBAORD,
                },
                {
                  label: "Charges Breakdown",
                  to: `${APP_ROUTES.CHARGES_BREAKDOWN}?month=${selectedMonth}&tab=${sourceView}`,
                },
                ...(sourceView !== "monthly"
                  ? [
                      {
                        label: sourceViewLabel,
                        to: `${APP_ROUTES.CHARGES_BREAKDOWN}?month=${selectedMonth}&tab=${sourceView}`,
                      },
                    ]
                  : []),
                { label: adviser.name },
              ]}
        />
        <header className="space-between-row">
          <div>
            {/* <p className="FP_Help_Text">Adviser</p> */}
            <h3 className="FP_H3">
              {adviser.name}{" "}
              <span className="FP_Help_Text">({adviser.email})</span>
            </h3>
            <p className="FP_Help_Text_Semibold">{adviser.brand}</p>
          </div>
          <div className="row gap-s">
            <PrimaryButton
              type="button"
              label="Add Charge"
              onClick={() => navigate(chargePageUrl())}
              disabled={chargesLocked}
            />
            <SecondaryButton
              type="button"
              label="Add Payment"
              onClick={() => navigate(paymentPageUrl())}
              disabled={selectedMonthClosed}
            />
          </div>
        </header>
        <div className="space-between-row gap-m">
          <RadioGroup
            name="adviser-charges-month"
            options={monthOptions}
            value={selectedMonth}
            onChange={selectMonth}
            inline
          />
          <SearchField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search"
          />
        </div>
        {selectedMonthClosed ? (
          <Banner
            type="info"
            message={
              canOverrideClosedCharges
                ? "This month is closed. Finance override is enabled for charges; payments remain read-only."
                : "This month is closed. Charges and payments are read-only."
            }
          />
        ) : null}
        {ioStatementError ? (
          <p className="FP_Help_Text text-error" role="alert">
            {ioStatementError}
          </p>
        ) : null}
        <SummaryCards
          cards={cards}
          grid="3"
          loading={statementValuesLoading}
        />
        <section ref={openingBalancesRef} className="fit-column gap-s width-100 align-left">
          <h4 className="FP_Subtitle_Semibold text-black">Opening Balance</h4>
          <div className="website-table-wrap table-scroll-container"><table className="website-table"><thead><tr>{["Opening Balance", "Total Commission", "Total Charged", "Closing Balance"].map((label) => <th key={label} className="FP_Help_Text_Semibold uppercase text-align-left">{label}</th>)}</tr></thead><tbody>{statementValuesLoading ? <tr><td className="FP_Content" colSpan="4">Loading…</td></tr> : <tr><td className={`FP_Help_Text text-align-left ${openingBalance < 0 ? "text-error" : "black"}`}>{money(openingBalance)}</td><td className="FP_Help_Text text-align-left black">{money(totalCommission)}</td><td className="FP_Help_Text text-align-left text-error">{money(totalCharged)}</td><td className={`FP_Help_Text_Semibold website-charge-group-divider text-align-left ${closingBalance < 0 ? "text-error" : "black"}`}>{money(closingBalance)}</td></tr>}</tbody></table></div>
        </section>
        <section ref={monthlyChargesRef} className="fit-column gap-s width-100 align-left">
          <h4 className="FP_Subtitle_Semibold text-black">
            Monthly Charges Breakdown
          </h4>
          <div className="website-table-wrap table-scroll-container">
            <table
              className="website-table"
              aria-label={`${adviser.name} monthly charges`}
            >
              <thead>
                <tr>
                  {[
                    ["chargeType", "Charge Type"],
                    ["date", "Date"],
                    ["description", "Charge Description"],
                    ["client", "Client"],
                    ["paraplanner", "Paraplanner"],
                    ["amount", "Amount"],
                  ].map(([key, label]) => (
                    <th
                      key={key}
                      className={`FP_Help_Text_Semibold uppercase ${label === "Amount" ? "website-charge-group-divider text-align-right" : ""}`}
                    >
                      <button
                        type="button"
                        className="table-heading-content table-sort-button"
                        onClick={() => toggleMonthlySort(key)}
                        aria-label={`Sort ${label}`}
                        aria-pressed={monthlySort.key === key}
                      >
                        {label} <SortIcon aria-hidden="true" />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chargesLoading ? (
                  <tr>
                    <td colSpan="6" className="FP_Content">
                      Loading…
                    </td>
                  </tr>
                ) : sortedMonthlyRecords.length ? (
                  sortedMonthlyRecords.map((row) => (
                    <tr key={row.id}>
                      <td className="FP_Help_Text black">
                        <div className="fit-column">
                          <span>{row.chargeType}</span>
                          {row.chargeType === "Paraplanning" && row.paraplanner ? (
                            <span className="FP_Help_Text monthly-charge-paraplanner">{row.paraplanner}</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="FP_Help_Text black">
                        {formatDate(row.date)}
                      </td>
                      <td className="FP_Help_Text black">
                        {feeEntryDescription(row)}
                      </td>
                      <td className="FP_Help_Text black">
                        {amlClientNames[row.id] || row.client || "-"}
                      </td>
                      <td className="FP_Help_Text black">
                        {row.paraplanner || row.assignedTo || "-"}
                      </td>
                      <td className="FP_Help_Text black website-charge-group-divider">
                        <div className="row width-full justify-flex-end align-center gap-m">
                          <span>{money(row.amountCharged)}</span>
                          <button
                            type="button"
                            className="table-row-action"
                            aria-label={`${row.recordType === "manual_charge" ? "Edit" : "Open"} ${row.description || row.chargeType}`}
                            onClick={() =>
                              row.recordType === "manual_charge"
                                ? navigate(chargePageUrl(row.id))
                                : row.sourcePath && navigate(row.sourcePath)
                            }
                            disabled={
                              (chargesLocked &&
                                row.recordType === "manual_charge") ||
                              (row.recordType !== "manual_charge" &&
                                !row.sourcePath)
                            }
                          >
                            {row.recordType === "manual_charge" ? (
                              <EditIcon aria-hidden="true" />
                            ) : (
                              <ArrowRightIcon aria-hidden="true" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="FP_Content">
                      No charges for this adviser this month.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        {miscellaneous.length > 0 && (
          <section ref={miscellaneousRef} className="fit-column gap-l width-100 align-left">
            <h4 className="FP_Subtitle_Semibold text-black">
              Miscellaneous Charges Breakdown
            </h4>
            <MiscellaneousChargesTable
              title="One-Time Charges"
              rows={sortedMiscellaneous(oneTimeCharges, "oneTime")}
              sort={miscSorts.oneTime}
              onSort={(key) => sortMiscellaneous("oneTime", key)}
              onEdit={(row) => navigate(chargePageUrl(row.id))}
              locked={chargesLocked}
            />
            <MiscellaneousChargesTable
              title="Recurring Charges"
              rows={sortedMiscellaneous(recurringCharges, "recurring")}
              sort={miscSorts.recurring}
              onSort={(key) => sortMiscellaneous("recurring", key)}
              onEdit={(row) => navigate(chargePageUrl(row.id))}
              locked={chargesLocked}
            />
          </section>
        )}
        <div ref={paymentsRef} />
        {advances.length > 0 && (
          <section ref={advancesRef} className="fit-column gap-s width-100 align-left">
            <h4 className="FP_Subtitle_Semibold text-black">Advances</h4>
            <div className="website-table-wrap table-scroll-container"><table className="website-table"><thead><tr>{[["date", "Date"], ["description", "Description"], ["amountPaid", "Advances"]].map(([key, label]) => <th key={key} className={`FP_Help_Text_Semibold uppercase ${key === "amountPaid" ? "website-charge-group-divider text-align-right" : ""}`}><button type="button" className={`table-heading-content table-sort-button ${key === "amountPaid" ? "width-full justify-flex-end" : ""}`} onClick={() => sortPayments("advances", key)}>{label} <SortIcon aria-hidden="true" /></button></th>)}</tr></thead><tbody>{sortedPayments(advances, "advances").map((payment) => <tr key={payment.id}><td className="FP_Help_Text black">{formatDate(payment.date)}</td><td className="FP_Help_Text black">{payment.description || "-"}</td><td className="FP_Help_Text black website-charge-group-divider"><div className="row width-full justify-flex-end align-center gap-m"><span>{money(payment.amountPaid)}</span>{!selectedMonthClosed ? <button type="button" className="table-row-action" aria-label="Edit advance" onClick={() => navigate(paymentPageUrl(payment.id, "advances"))}><EditIcon aria-hidden="true" /></button> : null}</div></td></tr>)}</tbody></table></div>
          </section>
        )}
        {additionalPayments.length > 0 && (
          <section ref={additionalPaymentsRef} className="fit-column gap-s width-100 align-left">
            <h4 className="FP_Subtitle_Semibold text-black">Additional Payments</h4>
            <div className="website-table-wrap table-scroll-container"><table className="website-table"><thead><tr>{[["date", "Date"], ["description", "Description"], ["amountPaid", "Additional Payment"]].map(([key, label]) => <th key={key} className={`FP_Help_Text_Semibold uppercase ${key === "amountPaid" ? "website-charge-group-divider text-align-right" : ""}`}><button type="button" className={`table-heading-content table-sort-button ${key === "amountPaid" ? "width-full justify-flex-end" : ""}`} onClick={() => sortPayments("additional", key)}>{label} <SortIcon aria-hidden="true" /></button></th>)}</tr></thead><tbody>{sortedPayments(additionalPayments, "additional").map((payment) => <tr key={payment.id}><td className="FP_Help_Text black">{formatDate(payment.date)}</td><td className="FP_Help_Text black">{payment.description || "-"}</td><td className="FP_Help_Text black website-charge-group-divider"><div className="row width-full justify-flex-end align-center gap-m"><span>{money(Math.abs(Number(payment.amountPaid || 0)))}</span>{!selectedMonthClosed ? <button type="button" className="table-row-action" aria-label="Edit additional payment" onClick={() => navigate(paymentPageUrl(payment.id, "additional-payments"))}><EditIcon aria-hidden="true" /></button> : null}</div></td></tr>)}</tbody></table></div>
          </section>
        )}
      </main>
    </div>
  );
};

export default withMonthlyPaymentsAccess(AdviserChargeBreakdown);
